import ky, { HTTPError, Options, type KyInstance } from "ky";

import { apiURL } from "../utils";

import { i18n } from "../modules/i18n";

import { THttpService } from "../types/httpService";

export type TApiServiceOptions = Options;

// expect Server to respond with a JSON object with a message property on bad request
// READ-MORE: https://github.com/sindresorhus/ky?tab=readme-ov-file#httperror
export type TApiServiceError<
  T = {
    message?: string;
    status?: number;
  },
> = HTTPError<T> & {
  response?: Response & {
    data?: {
      password_expired?: boolean;
      message?: string;
      status?: boolean;
      [key: string]: any;
    };
  };
  // Add strongly-typed errorResponse
  errorResponse?: any;
};

export class ApiService implements THttpService<Options> {
  public options: Options;
  protected kyInstance: KyInstance;

  constructor(options: Options = {}) {
    this.options = options;
    this.kyInstance = ky.create({
      hooks: {
        // READ-MORE: https://github.com/sindresorhus/ky?tab=readme-ov-file#hooksbeforerequest
        beforeRequest: [
          (request) => {
            request.headers.set("Accept-Language", i18n.currentLanguage);
          },
        ],
        // READ-MORE: https://github.com/sindresorhus/got/blob/main/documentation/9-hooks.md#beforeerror
        // READ-MORE: https://github.com/sindresorhus/ky/issues/412
        beforeError: [
          async (error) => {
            const { response } = error as TApiServiceError;
            let backendMessage = "";
            let errorBody = {};

            try {
              const body = await response.json();
              errorBody = body;
              if (body?.message) {
                backendMessage = body.message;
              }
              // Store the original response body in the error for later parsing
              if (error && typeof error === "object") {
                Object.defineProperty(error, "responseBody", {
                  value: body,
                  writable: true,
                  configurable: true,
                });
              }
            } catch {
              // fallback in case response isn't JSON
              backendMessage = i18n.t(
                [
                  `ERRORS.HTTP.STATUS_${response.status}`,
                  "errors:http.unknown",
                ],
                {
                  context: response.status,
                  status: response.status,
                },
              );
            }

            // generic http error message based on status code
            const httpErrorMessage =
              backendMessage ||
              i18n.t(
                [
                  `ERRORS.HTTP.STATUS_${response.status}`,
                  "errors:http.unknown",
                ],
                {
                  context: response.status,
                  status: response.status,
                },
              );

            // ERR_NON_2XX_3XX_RESPONSE
            // if server responded back with a bad request having body
            if (error instanceof HTTPError && response?.body) {
              error.message = httpErrorMessage;
              // Preserve the full error response for access to additional fields like password_expired
              Object.defineProperty(error, "response", {
                value: { ...response, data: errorBody },
                writable: true,
                configurable: true,
              });
            }

            return error;
          },
        ],
      },
      prefixUrl: apiURL().toString(),
      retry: {
        limit: 0,
      },
      timeout: 1000000,
      ...options,
    });
  }

  public extend(options: Options) {
    this.kyInstance = this.kyInstance.extend(options);
  }

  protected getKyInstance(): KyInstance {
    return this.kyInstance;
  }

  public async get<R = unknown>(url: string, options?: Options): Promise<R> {
    return this.kyInstance.get(url, options).json();
  }

  public async post<R = unknown, B = unknown>(
    url: string,
    body: B,
    options?: Options,
  ): Promise<R> {
    const isFormData =
      typeof FormData !== "undefined" && body instanceof FormData;
    return this.kyInstance
      .post(url, {
        ...(isFormData ? { body } : { json: body }),
        ...options,
      })
      .json();
  }

  public async put<R = unknown, B = unknown>(
    url: string,
    body?: B,
    options?: Options,
  ): Promise<R> {
    const isFormData =
      typeof FormData !== "undefined" && body instanceof FormData;
    return this.kyInstance
      .put(url, {
        ...(isFormData ? { body } : { json: body }),
        ...options,
      })
      .json();
  }

  public async patch<R = unknown, B = unknown>(
    url: string,
    body?: B,
    options?: Options,
  ): Promise<R> {
    return this.kyInstance.patch(url, { json: body, ...options }).json();
  }

  public async delete<R = unknown, B = unknown>(
    url: string,
    body?: B,
    options?: Options,
  ): Promise<R> {
    return this.kyInstance.delete(url, { json: body, ...options }).json();
  }

  public async getBlob(url: string, options?: Options): Promise<Blob> {
    return this.kyInstance.get(url, options).blob();
  }

  /**
   * POST binary download that also reads Content-Disposition for the filename.
   */
  public async postFile<B = unknown>(
    url: string,
    body?: B,
    options?: Options,
  ): Promise<{ blob: Blob; fileName: string | null }> {
    const isFormData =
      typeof FormData !== "undefined" && body instanceof FormData;
    const response = await this.kyInstance.post(url, {
      ...(body === undefined ? {} : isFormData ? { body } : { json: body }),
      ...options,
    });
    const blob = await response.blob();
    const disposition = response.headers.get("Content-Disposition");
    const fileName = parseContentDispositionFilename(disposition);
    return { blob, fileName };
  }
}

/** Parse filename from a Content-Disposition header value. */
export function parseContentDispositionFilename(
  header: string | null,
): string | null {
  if (!header) {
    return null;
  }

  const utf8Match = /filename\*\s*=\s*UTF-8''([^;]+)/i.exec(header);
  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(utf8Match[1].trim().replace(/^"|"$/g, ""));
    } catch {
      return utf8Match[1].trim().replace(/^"|"$/g, "");
    }
  }

  const plainMatch = /filename\s*=\s*("?)([^";]+)\1/i.exec(header);
  if (plainMatch?.[2]) {
    return plainMatch[2].trim();
  }

  return null;
}
